import { call, fork, put, race, select, takeEvery, take } from 'redux-saga/effects';
import { startSubmit, stopSubmit } from 'redux-form';
import { notificationsAdd } from 'virtues-bootstrap';
import { trim } from 'ramda';
import moment from 'moment';
import { push } from 'connected-react-router';
import frLocalization from 'moment/locale/fr';
import { show as showModal } from 'redux-modal';

import { FORM_NAMES } from 'constants/forms';
import { ACCEPT_USER_AGREEMENT } from 'constants/modals';

import { setHeaders, clearHeaders } from 'services/api';
import { getAuthData, setAuthData, clearAuthData } from 'services/auth';

import i18n from 'services/i18n';

import {
  authLogout,
  authLogin,
  requestAuthLogin,
  authUnauthorized,
  authSet,
  authSetupUser,
  requestAuthLoginSuccess,
  requestAuthLoginFailure,
} from 'store/actionCreators/auth';
import { userSetData, requestUserData, userAcceptAgreement } from 'store/actionCreators/user';
import { getUserPreferedLanguageCode, getUserIsSignedAgreement } from 'store/selectors/user';
import { PATIENT, ADMIN } from 'constants/userRoles';
import { getUniqId } from 'utilities';
import { createSagaRequestRace } from 'utilities/request';

export function* initial() {
  const { accessToken, idToken, encounter } = yield call(getAuthData);

  if (!accessToken) {
    yield put(authSet({ authenticated: false }));
    return;
  }

  yield put(authSetupUser({ accessToken, idToken, encounter }));
}

export function* onLogin(action) {
  yield put(stopSubmit(FORM_NAMES.login)); // will clear errors
  yield put(startSubmit(FORM_NAMES.login));

  const { username, password } = action.payload;
  yield put(
    requestAuthLogin({
      username: trim(username),
      password: trim(password),
    }),
  );
}

export function* onLogout() {
  yield put(authSet({ authenticated: false }));
  clearHeaders();
  yield call(clearAuthData);
  yield put(push('/login'));
}

// Called when request returns 401 status code
export function* onUnauthorized() {
  // yield put(showModal(TOKEN_EXPIRED));
  // yield take(sagaPatternOnModalClose(TOKEN_EXPIRED));
  yield put(authLogout());
}

export function* onRequestAuthLoginSuccess({ payload }) {
  yield put(stopSubmit(FORM_NAMES.login));
  const {
    response: { encounter, access_token: accessToken, id_token: idToken },
  } = payload;

  if (!encounter) {
    yield put(
      notificationsAdd({
        id: getUniqId(),
        variant: 'danger',
        title: i18n.t('common:requestError'),
        description: 'Encounter is empty',
      }),
    );
    return;
  }

  yield put(authSetupUser({ accessToken, idToken, encounter }));
}

export function* onRequestAuthLoginFailure() {
  yield put(
    stopSubmit(FORM_NAMES.login, {
      _error: i18n.t('common:login.incorrectCredentials'),
    }),
  );
}

export function* onAuthSetupUser(action) {
  // TODO: @sensone fix empty encounter
  const { accessToken, idToken, encounter } = action.payload;
  setHeaders({ accessToken, idToken });

  const [region, role, fhirId] = encounter.split(':');

  yield put(requestUserData({ id: fhirId, type: role === PATIENT ? 'Patient' : 'Practitioner' }));

  const { failure } = yield race(createSagaRequestRace(requestUserData));

  if (failure) {
    yield put(authUnauthorized());
    return;
  }

  // Set website language to prefered
  const preferedLanguage = yield select(getUserPreferedLanguageCode);

  if (preferedLanguage) {
    i18n.changeLanguage(preferedLanguage);
  }
  if (preferedLanguage === 'fr') {
    moment.updateLocale('fr', frLocalization);
  } else {
    moment.locale('en');
  }

  const isSignedAgreement = yield select(getUserIsSignedAgreement);
  if (!isSignedAgreement && role !== ADMIN) {
    yield put(showModal(ACCEPT_USER_AGREEMENT));
    const { success } = yield race({
      success: take(userAcceptAgreement),
      failure: take(authLogout),
    });
    if (!success) {
      return;
    }
  }
  yield call(setAuthData, {
    accessToken,
    encounter,
    idToken,
  });
  yield put(authSet({ authenticated: true }));
  yield put(userSetData({ region, role, fhirId }));
}

export default function* root() {
  yield fork(initial);
  yield takeEvery(authLogin, onLogin);
  yield takeEvery(authLogout, onLogout);
  yield takeEvery(authUnauthorized, onUnauthorized);
  yield takeEvery(requestAuthLoginSuccess, onRequestAuthLoginSuccess);
  yield takeEvery(requestAuthLoginFailure, onRequestAuthLoginFailure);
  yield takeEvery(authSetupUser, onAuthSetupUser);
}
